import apiClient from './client';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface User {
  id: string;
  email: string;
  fullName?: string;
  roles: string[];
  permissions: string[];
  companyId: string | null;
  secondaryCompanyIds?: string[];
  status?: string;
}

export interface Company {
  id: string;
  name: string;
  slug: string;
  /** 'PRIMARY_ENTERPRISE' (internal) | 'PARTNER' (external supplier). */
  type?: string;
  enabledPlugins: string[];
  /** Backend-computed effective plugin list (partner-aware, per active-company context). */
  effectivePlugins?: string[];
}

export interface LoginResponse {
  success: boolean;
  data?: {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
    user: User;
  };
  error?: string;
}

export interface RefreshResponse {
  success: boolean;
  data?: {
    accessToken: string;
    expiresIn: number;
  };
  error?: string;
}

export interface UserPreferences {
  theme?: 'light' | 'dark' | 'auto';
  notifications?: { email?: boolean; push?: boolean; sms?: boolean };
  display?: { density?: string; sidebarCollapsed?: boolean };
  [key: string]: unknown;
}

export interface AccessibleCompany {
  id: string;
  name: string;
  slug: string;
  type: string;
  status: string;
  isPrimary: boolean;
}

export interface AccessibleCompaniesResponse {
  success: boolean;
  data?: {
    companies: AccessibleCompany[];
  };
  error?: string;
}

export interface SwitchCompanyResponse {
  success: boolean;
  data?: {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
    user: User;
    company: Company;
  };
  error?: string;
}

export const authApi = {
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      const response = await apiClient.post<LoginResponse>('/api/auth/login', credentials);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed',
      };
    }
  },

  async logout(): Promise<void> {
    try {
      await apiClient.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    }
  },

  async refreshToken(refreshToken: string): Promise<RefreshResponse> {
    try {
      const response = await apiClient.post<RefreshResponse>('/api/auth/refresh', {
        refreshToken,
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Token refresh failed',
      };
    }
  },

  async getCurrentUser(): Promise<User | null> {
    try {
      const response = await apiClient.get<{ success: boolean; data: User }>('/api/auth/me');
      return response.data.data;
    } catch (error) {
      return null;
    }
  },

  async getCurrentCompany(): Promise<Company | null> {
    try {
      const response = await apiClient.get<{ success: boolean; data: Company | null }>('/api/auth/company');
      return response.data.data;
    } catch (error) {
      return null;
    }
  },

  async changePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      await apiClient.post('/api/auth/change-password', {
        currentPassword,
        newPassword,
      });

      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to change password',
      };
    }
  },

  getPreferences: async (): Promise<UserPreferences> => {
    const res = await apiClient.get('/api/auth/preferences');
    return res.data.data.preferences ?? {};
  },

  updatePreferences: async (patch: UserPreferences): Promise<UserPreferences> => {
    const res = await apiClient.put('/api/auth/preferences', { preferences: patch });
    return res.data.data.preferences ?? {};
  },

  async getAccessibleCompanies(): Promise<AccessibleCompaniesResponse> {
    try {
      const response = await apiClient.get<AccessibleCompaniesResponse>(
        '/api/auth/accessible-companies'
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get accessible companies',
      };
    }
  },

  async switchCompany(targetCompanyId: string): Promise<SwitchCompanyResponse> {
    try {
      const response = await apiClient.post<SwitchCompanyResponse>(
        '/api/auth/switch-company',
        { targetCompanyId }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to switch company',
      };
    }
  },

  async forgotPassword(email: string): Promise<{ success: boolean; error?: string }> {
    try {
      await apiClient.post('/api/auth/forgot-password', { email });
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to send reset email',
      };
    }
  },

  async resetPassword(token: string, newPassword: string): Promise<{ success: boolean; error?: string }> {
    try {
      await apiClient.post('/api/auth/reset-password', { token, newPassword });
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to reset password',
      };
    }
  },
};
